import { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import { membersAPI, beneficiariesAPI } from './services/api';
import Layout from './components/Layout';

// ── Auth pages ────────────────────────────────────────────────────────────────
import Login    from './pages/auth/Login';
import Register from './pages/auth/Register';

// ── Member pages ──────────────────────────────────────────────────────────────
import MemberDashboard    from './pages/member/Dashboard';
import MemberPayments     from './pages/member/Payments';
import MemberPaymentDetail from './pages/member/PaymentDetail';
import MemberProfile      from './pages/member/Profile';
import ManagerProfile     from './pages/manager/Profile';
import MemberBeneficiaries from './pages/member/Beneficiaries';
import MemberReceipts     from './pages/member/Receipts';

// ── Manager pages (admin / finance) ──────────────────────────────────────────
import ManagerDashboard    from './pages/manager/Dashboard';
import ManagerPayments     from './pages/manager/Payments';
import ManagerPaymentDetail from './pages/manager/PaymentDetail';
import Members             from './pages/manager/Members';
import PendingAdmissions   from './pages/manager/PendingAdmissions';
import MemberDetail        from './pages/manager/MemberDetail';
import Claims              from './pages/manager/Claims';
import ClaimDetail         from './pages/manager/ClaimDetail';
import Reports             from './pages/manager/Reports';
import FeeConfig           from './pages/manager/FeeConfig';
import DeceasedMembers     from './pages/manager/DeceasedMembers';
import Natumia             from './pages/manager/Natumia';
import SystemSettings      from './pages/manager/SystemSettings';
import StaffManagement    from './pages/manager/StaffManagement';
import StaffPermissions from './pages/manager/StaffPermissions';
import PayoutRegister     from './pages/manager/PayoutRegister';
import AuditTrail         from './pages/manager/AuditTrail';
import CrdbRequests       from './pages/manager/CrdbRequests';
import BankReconciliation from './pages/manager/BankReconciliation';
import ErrorLogs          from './pages/manager/ErrorLogs';
import Notifications      from './pages/manager/Notifications';

// ── Shared pages ──────────────────────────────────────────────────────────────
import PublicUpdate from './pages/PublicUpdate';
import HealthInsuranceSurvey from './pages/HealthInsuranceSurvey';
import HealthInsurance from './pages/manager/HealthInsurance';

// ─────────────────────────────────────────────────────────────────────────────
// Route guards
// ─────────────────────────────────────────────────────────────────────────────

/** Redirects to /login if not authenticated. */
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

/** Redirects to / if already authenticated. */
const PublicRoute = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  return isAuthenticated ? <Navigate to="/" replace /> : children;
};

/**
 * Shows allowed page or redirects to / if neither the role nor an explicitly
 * granted permission (see PermissionsController / Staff & Approvers →
 * Permissions) allows it. requiredPermission lets a non-admin user reach a
 * page their base role wouldn't normally unlock.
 */
const RoleRoute = ({ children, allowedRoles, requiredPermission }) => {
  const { user } = useAuthStore();
  if (!user) return <Navigate to="/login" replace />;
  if (allowedRoles.includes(user.role)) return children;
  const perms = user.permissions || [];
  const keys = Array.isArray(requiredPermission) ? requiredPermission : [requiredPermission];
  if (requiredPermission && keys.some((k) => perms.includes(k))) return children;
  return <Navigate to="/" replace />;
};

/**
 * Shows a different page component based on the current user's role.
 * - member    → memberPage
 * - admin/finance → managerPage
 * Use this for routes shared between roles (e.g. /payments).
 */
const RoleBasedPage = ({ memberPage: MemberPage, managerPage: ManagerPage, requiredPermission }) => {
  const { user } = useAuthStore();
  if (!user) return <Navigate to="/login" replace />;
  if (['admin', 'finance', 'board'].includes(user.role)) return <ManagerPage />;
  const perms = user.permissions || [];
  const keys = Array.isArray(requiredPermission) ? requiredPermission : [requiredPermission];
  if (requiredPermission && keys.some((k) => perms.includes(k))) return <ManagerPage />;
  if (user.role === 'member') return <MemberPage />;
  return <Navigate to="/" replace />;
};

/**
 * Members must have their Profile (photo, phone, gender, date of birth, chapter)
 * and Beneficiaries (at least one next of kin with a beneficiary selected) filled
 * in before they can access Payments — redirects them to whichever is missing.
 */
/** Joins ['a'] -> "a", ['a','b'] -> "a and b", ['a','b','c'] -> "a, b and c". */
function joinNaturally(items) {
  if (items.length <= 1) return items[0] || '';
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

const RequireMemberSetup = ({ children }) => {
  const [status, setStatus] = useState('loading'); // loading | ok | needs-profile | needs-beneficiaries
  const [notice, setNotice] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const member = await membersAPI.me();
        const missing = [];
        if (!member.picture) missing.push('profile picture');
        if (!member.phone) missing.push('phone number');
        if (!member.gender) missing.push('gender');
        if (!member.date_of_birth) missing.push('date of birth');
        if (!member.chapter) missing.push('chapter');

        if (missing.length > 0) {
          if (!cancelled) {
            setNotice(`Please add your ${joinNaturally(missing)} before making a payment.`);
            setStatus('needs-profile');
          }
          return;
        }

        const benResp = await beneficiariesAPI.getByMember(member.id);
        const hasKin = (benResp.beneficiaries || []).length > 0;
        const hasSelectedBeneficiary = !!benResp.selected_installment_one_beneficiary;
        if (!hasKin) {
          if (!cancelled) {
            setNotice('Please add a next of kin before making a payment.');
            setStatus('needs-beneficiaries');
          }
          return;
        }
        if (!hasSelectedBeneficiary) {
          if (!cancelled) {
            setNotice('Please select a beneficiary from your next of kin before making a payment.');
            setStatus('needs-beneficiaries');
          }
          return;
        }

        if (!cancelled) setStatus('ok');
      } catch {
        if (!cancelled) setStatus('ok'); // don't lock a member out over a transient API error
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (status === 'loading') {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-orange-500 border-t-transparent" />
      </div>
    );
  }

  if (status === 'needs-profile') {
    return <Navigate to="/profile" replace state={{ notice }} />;
  }

  if (status === 'needs-beneficiaries') {
    return <Navigate to="/beneficiaries" replace state={{ notice }} />;
  }

  return children;
};

// ─────────────────────────────────────────────────────────────────────────────
// App routes
// ─────────────────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <Router>
      <Routes>
        {/* ── Public / auth ── */}
        <Route path="/login"    element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
        <Route path="/public/update/:token" element={<PublicUpdate />} />
        <Route path="/health-insurance-survey" element={<HealthInsuranceSurvey />} />
        <Route path="/health-survey" element={<HealthInsuranceSurvey />} />

        {/* ── Protected app ── */}
        <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>

          {/* Dashboard — member sees MemberDashboard, manager sees ManagerDashboard */}
          <Route index element={
            <RoleBasedPage memberPage={MemberDashboard} managerPage={ManagerDashboard} />
          } />

          {/* Payments — member sees own list, manager sees all + create */}
          <Route path="payments" element={
            <RoleBasedPage
              memberPage={() => <RequireMemberSetup><MemberPayments /></RequireMemberSetup>}
              managerPage={ManagerPayments}
              requiredPermission={['payments', 'bank_reconciliation']}
            />
          } />

          {/* Payment detail — member sees CRDB tip, manager sees full info */}
          <Route path="payments/:id" element={
            <RoleBasedPage
              memberPage={() => <RequireMemberSetup><MemberPaymentDetail /></RequireMemberSetup>}
              managerPage={ManagerPaymentDetail}
              requiredPermission={['payments', 'bank_reconciliation']}
            />
          } />

          {/* ── Profile — member vs manager ── */}
          <Route path="profile" element={
            <RoleBasedPage memberPage={MemberProfile} managerPage={ManagerProfile} />
          } />
          <Route path="receipts"      element={<RoleRoute allowedRoles={['member']}><MemberReceipts /></RoleRoute>} />
          <Route path="beneficiaries" element={<RoleRoute allowedRoles={['admin', 'member']}><MemberBeneficiaries /></RoleRoute>} />

          {/* ── Manager-only routes (admin) ── */}
          <Route path="members"          element={<RoleRoute allowedRoles={['admin']} requiredPermission={['members', 'member_invite', 'member_status']}><Members /></RoleRoute>} />
          <Route path="pending-admissions" element={<RoleRoute allowedRoles={['admin']} requiredPermission={['pending_admissions']}><PendingAdmissions /></RoleRoute>} />
          <Route path="members/:id"      element={<RoleRoute allowedRoles={['admin']} requiredPermission={['members', 'member_invite', 'member_status']}><MemberDetail /></RoleRoute>} />
          <Route path="deceased-members" element={<RoleRoute allowedRoles={['admin']} requiredPermission={['members']}><DeceasedMembers /></RoleRoute>} />
          <Route path="natumia"          element={<RoleRoute allowedRoles={['admin']}><Natumia /></RoleRoute>} />

          {/* ── Manager-only routes (admin + finance) ── */}
          <Route path="claims"     element={<RoleRoute allowedRoles={['admin', 'finance', 'board']} requiredPermission={['claims']}><Claims /></RoleRoute>} />
          <Route path="claims/:id" element={<RoleRoute allowedRoles={['admin', 'finance', 'board']} requiredPermission={['claims']}><ClaimDetail /></RoleRoute>} />
          <Route path="reports"    element={<RoleRoute allowedRoles={['admin', 'finance']} requiredPermission={['reports']}><Reports /></RoleRoute>} />
          <Route path="payout-register"  element={<RoleRoute allowedRoles={['admin', 'finance']} requiredPermission={['claims']}><PayoutRegister /></RoleRoute>} />
          <Route path="staff"            element={<RoleRoute allowedRoles={['admin']} requiredPermission={['staff']}><StaffManagement /></RoleRoute>} />
          <Route path="staff/:userId/permissions" element={<RoleRoute allowedRoles={['admin']}><StaffPermissions /></RoleRoute>} />
          <Route path="audit-trail"      element={<RoleRoute allowedRoles={['admin']} requiredPermission={['audit_trail']}><AuditTrail /></RoleRoute>} />
          <Route path="crdb-requests"    element={<RoleRoute allowedRoles={['admin']} requiredPermission={['crdb_requests']}><CrdbRequests /></RoleRoute>} />
          <Route path="bank-reconciliation" element={<RoleRoute allowedRoles={['admin']} requiredPermission={['bank_reconciliation']}><BankReconciliation /></RoleRoute>} />
          <Route path="error-logs"       element={<RoleRoute allowedRoles={['admin']} requiredPermission={['error_logs']}><ErrorLogs /></RoleRoute>} />
          <Route path="fee-config"       element={<RoleRoute allowedRoles={['admin']} requiredPermission={['fee_config', 'payment_methods']}><FeeConfig /></RoleRoute>} />
          <Route path="system-settings"  element={<RoleRoute allowedRoles={['admin']} requiredPermission={['system_settings', 'zones', 'vpn_status']}><SystemSettings /></RoleRoute>} />
          <Route path="health-insurance" element={<RoleRoute allowedRoles={['admin', 'finance']} requiredPermission={['health_insurance', 'reports']}><HealthInsurance /></RoleRoute>} />
          <Route path="notifications"    element={<RoleRoute allowedRoles={['admin', 'finance']} requiredPermission={['notifications']}><Notifications /></RoleRoute>} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}
